/**
 * Chat API endpoints
 */

import { apiClient } from './client';
import type {
  Chat,
  ChatResponse,
  ChatFilters,
  SendMessageRequest,
  ScheduledMessage,
  DraftVariant,
} from '@/types/chat';

export const chatApi = {
  /**
   * Get chats with pagination and filters
   */
  getChats: async (params: {
    page?: number;
    page_size?: number;
    sort?: string;
    page_token?: string;
  } & ChatFilters): Promise<ChatResponse> => {
    const response = await apiClient.get<ChatResponse>('/chats/', { 
      params: params,
      paramsSerializer: {
        serialize: (p: any) => {
          const usp = new URLSearchParams();
          Object.entries(p).forEach(([key, value]) => {
            if (value === undefined || value === null) return;
            const paramKey = key === 'operator' ? 'project_owner' : key;
            if (Array.isArray(value)) {
              value.forEach((v) => usp.append(paramKey, String(v)));
            } else {
              usp.append(paramKey, String(value));
            }
          });
          return usp.toString();
        },
      },
    });
    
    return response.data;
  },

  /**
   * Get single chat by ID
   */
  getChat: async (id: string): Promise<Chat> => {
    const response = await apiClient.get<Chat>(`/chats/${id}/`);
    return response.data;
  },

  /**
   * Ask AI a question about this chat and prospect
   */
  askAiHelper: async (
    chatId: string,
    question: string
  ): Promise<{ answer: string; justification?: string }> => {
    const response = await apiClient.post<{
      output: { answer: string; justification?: string };
    }>(`/chats/${chatId}/ai-helper/`, { question });
    if (!response.data.output) {
      throw new Error('Invalid response format: missing output field');
    }
    return response.data.output;
  },

  /**
   * Sorted unique matrix strategy names for the custom-draft dropdown.
   */
  getDraftStrategyNames: async (): Promise<string[]> => {
    const response = await apiClient.get<{ output: { strategy_names: string[] } }>(
      '/chats/meta/draft-strategies/',
    );
    return response.data?.output?.strategy_names ?? [];
  },

  /**
   * On-demand single draft (carousel slot 4); not persisted on the chat row.
   * Send strategy_name (matrix label) or custom_prompt (own strategy), not both; backend ignores strategy_name if custom_prompt is set.
   */
  generateCustomDraft: async (
    chatId: string,
    params: { strategy_name?: string; custom_prompt?: string },
  ): Promise<DraftVariant> => {
    const response = await apiClient.post<{ output: { variant: DraftVariant } }>(
      `/chats/${chatId}/custom-draft/`,
      params,
    );
    if (!response.data.output?.variant) {
      throw new Error('Invalid response format: missing variant');
    }
    return response.data.output.variant;
  },

  /**
   * Send reply to chat
   */
  sendReply: async (chatId: string, message: SendMessageRequest): Promise<Chat> => {
    const response = await apiClient.post<{ output: Chat }>(`/chats/${chatId}/reply/`, message);
    if (!response.data.output) {
      throw new Error('Invalid response format: missing output field');
    }
    return response.data.output;
  },

  /**
   * Schedule a message to be sent at a future date/time
   */
  scheduleMessage: async (chatId: string, content: string, scheduledAt: string): Promise<ScheduledMessage> => {
    const response = await apiClient.post<{ output: ScheduledMessage }>(`/chats/${chatId}/schedule-message/`, {
      content,
      scheduled_at: scheduledAt,
    });
    if (!response.data.output) {
      throw new Error('Invalid response format: missing output field');
    }
    return response.data.output;
  },

  /**
   * Get scheduled messages for a chat
   */
  getScheduledMessages: async (chatId: string): Promise<ScheduledMessage[]> => {
    const response = await apiClient.get<{ output: { items: ScheduledMessage[] } }>(`/chats/${chatId}/scheduled-messages/`);
    return response.data?.output?.items ?? [];
  },

  /**
   * Cancel/delete a scheduled message
   */
  deleteScheduledMessage: async (chatId: string, jobUuid: string): Promise<void> => {
    await apiClient.delete(`/chats/${chatId}/scheduled-messages/${jobUuid}/`);
  },

  /**
   * Mark chat as seen
   */
  markAsSeen: async (chatId: string): Promise<Chat> => {
    const response = await apiClient.patch<{ output: Chat }>(`/chats/${chatId}/update/`, { 
      seen_last_message: true 
    });
    if (!response.data.output) {
      throw new Error('Invalid response format: missing output field');
    }
    return response.data.output;
  },

  /**
   * Mark chat as important
   */
  markAsImportant: async (chatId: string, isImportant: boolean): Promise<Chat> => {
    const response = await apiClient.patch<{ output: Chat }>(`/chats/${chatId}/update/`, { 
      is_important: isImportant 
    });
    if (!response.data.output) {
      throw new Error('Invalid response format: missing output field');
    }
    return response.data.output;
  },

  /**
   * Mark chat as archived
   */
  markAsArchived: async (chatId: string, isArchived: boolean): Promise<Chat> => {
    const response = await apiClient.patch<{ output: Chat }>(`/chats/${chatId}/update/`, { 
      is_archived: isArchived 
    });
    if (!response.data.output) {
      throw new Error('Invalid response format: missing output field');
    }
    return response.data.output;
  },

  /**
   * Mark chat as unread
   */
  markAsUnread: async (chatId: string): Promise<Chat> => {
    const response = await apiClient.patch<{ output: Chat }>(`/chats/${chatId}/update/`, { 
      mark_as_unread: true 
    });
    if (!response.data.output) {
      throw new Error('Invalid response format: missing output field');
    }
    return response.data.output;
  },

  /**
   * Update chat notes
   */
  updateNotes: async (chatId: string, notes: string): Promise<Chat> => {
    const response = await apiClient.patch<{ output: Chat }>(`/chats/${chatId}/update/`, { 
      notes 
    });
    if (!response.data.output) {
      throw new Error('Invalid response format: missing output field');
    }
    return response.data.output;
  },

  /**
   * Update chat tags
   */
  updateTags: async (chatId: string, tags: string[]): Promise<Chat> => {
    const response = await apiClient.patch<{ output: Chat }>(`/chats/${chatId}/update/`, { 
      tags 
    });
    if (!response.data.output) {
      throw new Error('Invalid response format: missing output field');
    }
    return response.data.output;
  },

  /**
   * Get all tags
   */
  getTags: async (): Promise<{ title: string; key: string }[]> => {
    const response = await apiClient.get<any>('/tags/');
    const items = response.data?.output?.items ?? [];
    return items.map((item: any) => ({ title: item.title, key: item.title }));
  },

  /**
   * Create a new tag (admin only)
   */
  createTag: async (title: string): Promise<{ title: string; key: string }> => {
    const response = await apiClient.post<any>('/tags/create/', { title });
    const output = response.data?.output ?? {};
    return { title: output.title, key: output.title };
  },


  /**
   * Get available streams for filter
   */
  getStreams: async (): Promise<{ name: string; uuid: string }[]> => {
    const response = await apiClient.get<any>('/streams/');
    const items = response.data?.output?.items ?? [];
    // Deduplicate by uuid
    const map = new Map<string, { name: string; uuid: string }>();
    for (const item of items) {
      const uuid = item.key;
      if (!uuid || map.has(uuid)) continue;
      map.set(uuid, { name: item.name, uuid });
    }
    return Array.from(map.values());
  },

  /**
   * Get available project owners for filter with pagination
   */
  getProjectOwners: async (params?: { search?: string; page_token?: string; max_results?: number }): Promise<{
    items: { name: string; uuid: string }[];
    next_page_token?: string | null;
    previous_page_token?: string | null;
    total_results?: number | null;
  }> => {
    const queryParams: any = {};
    if (params?.search) {
      queryParams.search = params.search;
    }
    if (params?.page_token) {
      queryParams.page_token = params.page_token;
    }
    if (params?.max_results !== undefined) {
      queryParams.max_results = params.max_results;
    } else {
      queryParams.max_results = 50; // Default to 50 for better performance
    }
    // Use users_list endpoint to fetch users current user has access to
    const response = await apiClient.get<any>('/users/', { params: queryParams });
    const output = response.data?.output ?? {};
    const items = (output.items ?? []).map((item: any) => ({ name: item.name, uuid: item.key }));
    return {
      items,
      next_page_token: output.next_page_token ?? null,
      previous_page_token: output.previous_page_token ?? null,
      total_results: output.total_results ?? null,
    };
  },

  /**
   * Get available message categories for filter
   */
  getMessageCategories: async (): Promise<{ key: string; label: string }[]> => {
    const response = await apiClient.get<any>('/message-categories/');
    const items = response.data?.output?.items ?? [];
    return items.map((item: any) => ({ key: item.key, label: item.label }));
  },

  /**
   * Send email notification
   */
  sendEmailNotification: async (data: {
    fromEmail: string;
    fromName: string;
    to: string;
    cc: string[];
    subject: string;
    message: string;
    chatUuid?: string;
  }): Promise<void> => {
    await apiClient.post('/email/notification/', {
      from_email: data.fromEmail,
      from_name: data.fromName,
      to_email: data.to,
      cc_emails: data.cc,
      subject: data.subject,
      message: data.message,
      chat_uuid: data.chatUuid,
    });
  },
};